import {
  DeepPartial,
  FieldErrors,
  ResolverResult,
  UnpackNestedValue,
} from 'react-hook-form';

import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import {
  getServerTime,
  isAfter,
  isBefore,
  isSameDay,
  parseIsoDate,
  startOfDay,
} from '../../../../utils/dateUtil';
import { parseAndValidateCorrectTimeAndDivisibleBy5Minutes } from '../../../../utils/dateValidator';
export type MassCancelFormValues = {
  date: string;
  /**
   * Optional inclusive last day of the cancelled range. Only a superadmin may
   * set it. Empty means the cancel covers the single {@link date} only.
   */
  toDate?: string;
  fullDay: boolean;
  fromTime: string;
  toTime: string;
  clearApptTemplate: boolean;
};

export const useMassCancelValidation = () => {
  const { t } = useCommonTranslation();
  return (
    submitValues: UnpackNestedValue<MassCancelFormValues>,
  ): ResolverResult<MassCancelFormValues> => {
    const errors: DeepPartial<FieldErrors<MassCancelFormValues>> = {};
    const serverNow = getServerTime();
    const todayStartOfDay = startOfDay(serverNow);
    let date: Date | undefined;
    if (!submitValues.date) {
      errors.date = { message: t('must-not-empty') };
    } else {
      date = parseIsoDate(submitValues.date);
      if (isBefore(date, todayStartOfDay)) {
        errors.date = {
          message: t('must-not-be-in-past'),
        };
      }
    }

    if (submitValues.toDate) {
      const toDate = parseIsoDate(submitValues.toDate);
      if (isBefore(toDate, todayStartOfDay)) {
        errors.toDate = { message: t('must-not-be-in-past') };
      } else if (date && isBefore(toDate, date)) {
        errors.toDate = {
          message: t('must-be-same-or-after', { column: t('date-from') }),
        };
      }
    }

    if (!submitValues.fullDay) {
      let fromTimeDate: Date | undefined = undefined;
      if (!submitValues.fromTime) {
        errors.fromTime = { message: t('must-not-empty') };
      } else {
        const { message, parsedDate } =
          parseAndValidateCorrectTimeAndDivisibleBy5Minutes({
            input: submitValues.fromTime,
            t,
          });
        if (message !== undefined) {
          errors.fromTime = { message };
        } else {
          if (
            date &&
            isSameDay(date, todayStartOfDay) &&
            isBefore(parsedDate, serverNow)
          ) {
            errors.fromTime = { message: t('must-be-in-future') };
          } else {
            fromTimeDate = parsedDate;
          }
        }
      }

      if (!submitValues.toTime) {
        errors.toTime = { message: t('must-not-empty') };
      } else {
        const { message, parsedDate } =
          parseAndValidateCorrectTimeAndDivisibleBy5Minutes({
            input: submitValues.toTime,
            t,
          });
        if (message !== undefined) {
          errors.toTime = { message };
        } else if (fromTimeDate && !isAfter(parsedDate, fromTimeDate)) {
          errors.toTime = {
            message: t('must-be-after', { column: t('time-from') }),
          };
        }
      }
    }

    return {
      values: submitValues,
      errors,
    };
  };
};
